import { useEffect, useMemo, useRef } from 'react';
import { useMatches, useNavigation } from 'react-router';

import {
  startRouteLoadingAnimation,
  stopRouteLoadingAnimation,
} from 'utils/ui/routeLoadingAnimation';

function isPromise(value: unknown): value is Promise<unknown> {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'then' in value &&
      typeof value.then === 'function',
  );
}

export function useTrackNavigationState() {
  const { state } = useNavigation();
  const matches = useMatches();

  const promises = useMemo(() => {
    return matches
      .flatMap((match) => {
        if (!match.loaderData || typeof match.loaderData !== 'object')
          return [];

        return Object.values<unknown>(
          match.loaderData as Record<string, unknown>,
        ).filter((value) => {
          if (!isPromise(value)) return false;

          const isFulfilled = 'status' in value && value.status === 'fulfilled';
          return !isFulfilled;
        });
      })
      .filter(Boolean);
  }, [matches]);

  const fulfilledRef = useRef(false);
  if (state === 'loading') fulfilledRef.current = false;

  const isLoading =
    state === 'loading' || (promises.length > 0 && !fulfilledRef.current);

  useEffect(() => {
    if (fulfilledRef.current || promises.length === 0) return;
    let canceled = false;

    void Promise.all(promises).then(() => {
      if (!canceled) {
        window.loadingRoute = false;
        stopRouteLoadingAnimation();
        fulfilledRef.current = true;
      }
    });

    return () => {
      canceled = true;
    };
  }, [promises, fulfilledRef]);

  if (typeof window !== 'undefined') {
    window.loadingRoute = isLoading;

    if (isLoading) {
      startRouteLoadingAnimation();
    } else {
      stopRouteLoadingAnimation();
    }
  }
}
